import * as React from 'react';
import { InputHTMLAttributes, ReactNode } from 'react';
import { DateTimePicker } from 'react-widgets';
import { BaseFieldProps, Field, GenericField, WrappedFieldProps } from 'redux-form';
import { FieldWrapper, FieldWrapperConfig } from './FieldWrapper';
import styles from '../assets/wrapper-input.module.scss';
import moment from 'moment';
import classNames from 'classnames';

/**
 * Input props
 */
export interface DateComponentProps extends InputHTMLAttributes<HTMLInputElement> {
    inputStyle?: AnyObject | string;
    inputErrorStyle?: AnyObject | string;
    wrapperConfig?: FieldWrapperConfig;
    disabled?: boolean;
    time?: boolean;
    max?: any;
    min?: any;
    date?: boolean;
    step?: number;
    formatType?: string;
    children?: ReactNode;
    inputProps?: any; // Prevent manual editing
}

type InputProps = DateComponentProps & WrappedFieldProps;

/**
 * InputComponent which renders wrapped input field with label and error
 *
 * @constructor
 */
const DateComponent: React.FunctionComponent<InputProps> = (props: InputProps) => {
    const hasError = !props.meta.valid && props.meta.touched;
    const handleChange = (date?: Date | undefined, dateStr?: string | undefined) => {
        if (date) {
            if (props.time) {
                const dateTime = dateStr ? moment(dateStr, 'h:m A').format() : new Date();
                props.input.onChange(dateTime);
            } else {
                // Store as plain YYYY-MM-DD to avoid timezone offset shifting the date
                props.input.onChange(moment(date).format('YYYY-MM-DD'));
            }
        } else {
            props.input.onChange(date);
        }
    };

    return (
        <FieldWrapper
            {...props.wrapperConfig}
            classes={props.wrapperConfig?.classes || {}}
            name={props.input.name}
            meta={props.meta}
        >
            <DateTimePicker
                containerClassName={classNames(hasError && props.inputErrorStyle, styles.datePickerFull)}
                onChange={handleChange}
                max={props.max}
                min={props.min}
                value={!props.input.value ? undefined : moment(props.input.value).toDate()}
                time={props.time ? true : false}
                date={props.date ? true : false}
                step={props.step}
                disabled={props.disabled}
                format={props.formatType ? props.formatType : 'LL'}
                inputProps={props.inputProps} // Prevent manual editing
            />
            {props.children}
        </FieldWrapper>
    );
};

DateComponent.defaultProps = {
    inputStyle: 'form-control',
    wrapperConfig: {
        classes: {
            inputContainer: 'Date-wrap',
        },
    },
    inputErrorStyle: 'border-danger',
};

const FieldDateComponent = Field as new () => GenericField<DateComponentProps>;

type FieldProps = BaseFieldProps<DateComponentProps> & DateComponentProps;

/**
 * FieldInput component which extends GenericField from redux form and renders input field
 * (InputComponent) with all needed props
 *
 * @param props
 * @constructor
 */
export const FieldDate: React.FunctionComponent<FieldProps> = (props: FieldProps) => (
    <FieldDateComponent {...props} component={DateComponent} />
);
